// GENERATED by helm-console/scripts/ensure-icons.mjs — DO NOT EDIT.
// Source of truth: helm-bridge/src/client/icons-quarterdeck.ts
// (itself generated from lucide-static by helm-bridge/scripts/gen-icon-sprite.mjs).
// Regenerated on every console build; edits here are overwritten.
// The console and the release UI share ONE icon set on purpose — see the
// header of ensure-icons.mjs.
// icons-quarterdeck.ts — QUARTERDECK's ICON SET.
//
// THE SHARED ID CONTRACT (same as every set). A style does not get its own id
// space: it answers the SAME symbol ids the Blueprint sprite defines, so every
// call site in the app is unchanged and a style switch is a sprite swap, not a
// refactor. Two rules make that safe, and scripts/check-icon-refs.mjs enforces
// both with measured counts:
//
//   1. NO ORPHANS. Every id below must exist in the Blueprint sprite. An id
//      only this set defines would render for QUARTERDECK and BLANK for
//      everyone else — the silent-blank failure the icons gate exists to
//      refuse.
//   2. COMPLETE COVERAGE (owner ruling 2026-09-07 — this SUPERSEDES the
//      "named, counted fallback" rule this file shipped with). The owner saw
//      the live product under a styled set and found a rail of MIXED glyphs:
//      some the style's own, some smooth Blueprint art. A styled set is now
//      100% its own glyphs or `icons:check` is RED; the Blueprint fallback
//      survives only as a build-time diagnostic. Every one of the REQUIRED
//      ids the Blueprint sprite defines is drawn below, and the number is
//      DERIVED by the gate from the sprite — still do not pin it here.
//
// THE DRAWING RULES (spec §6, ruling R21). A BURIN CUT HAS NO ROUND ENDS.
// Uniform 1.5px stroke on the 24 grid, `stroke-linejoin: bevel`,
// `stroke-linecap: butt` — and the weight and the joins are NOT set here:
// styles.css applies them to `.icon` under this style, so one number governs
// the whole set and the treatment reaches every glyph this file never
// touches. That is the entire "engraved" treatment, and it is specifiable —
// the base attempt's "occasional small hand-cut irregularity" cannot be built,
// and an inconsistent irregularity is exactly what reads as a bug.
// (Against Glass's 1.25 round, Starship's 1.5 miter and Destroyer's 2px
// square + stencil bridges: four distinguishable constructions.)
//
// METAPHOR DISCIPLINE — the rule that keeps this from becoming a costume, and
// how the completion ruling changes it. The original text said `trash-2`,
// `plus`, `x`, `pencil`, `copy`, `search` are DELIBERATELY NOT re-cut. That
// exemption is what produced the mixed rail the owner rejected, so it is
// retired — but the REASON behind it is not. Those ids are re-cut here with
// their CONVENTIONAL SILHOUETTE INTACT and only the construction changed:
// ruled straight segments, bevelled corners, serifed terminals, octagons where
// Blueprint uses a circle. `search` is still a lens on a handle; `trash-2` is
// still a bin with two staves. Nothing about the meaning moved.
// Genuinely nautical substitutions stay reserved for DOMAIN AND BRANDING spots
// where the metaphor is exact and the reading is not at risk — `database` is a
// hooped cask, `pin` a belaying pin, `antenna` a signal mast flying two
// pennants, `link` two chain shackles — and are never load-bearing for a
// primary action's meaning.
//
// NO ZERO-LENGTH DOTS. `stroke-linecap: butt` is this style's signature, and
// it makes Blueprint's `<path d="M12 16h.01" />` dot idiom render a 0.01×1.5
// sliver — invisible. Measured on the shipped `circle-alert`, whose warning
// dot was missing from this set from the day it landed. A dot in QUARTERDECK
// is `<circle r=".9" />`, never `h.01`.
//
// THE COMPASS ROSE IS DIRECTIONAL GRAMMAR. Its four spikes double as the
// generic up / down / next / back arrows throughout the app, which ties every
// piece of nav chrome back to the logo mark. The rose is QUARTERDECK's
// exclusively (ruling R2.1): Destroyer's sweep rotates around range rings and
// a degree scale and has no cardinal points at all.

/** The four cardinal spikes of the rose, as kites meeting at the centre. N is
 * LONGEST, per real compass-rose convention — the asymmetry is what stops the
 * mark reading as a plus sign at 16px, and it is the same asymmetry the logo
 * and the favicon carry (ruling R22: cross-with-a-dot). */
const SPIKE_N = '<path d="M12 1.2 13.9 9.2 12 12 10.1 9.2Z" />';
const SPIKE_S = '<path d="M12 20.4 10.7 14.6 12 12 13.3 14.6Z" />';
const SPIKE_E = '<path d="M20.4 12 14.6 13.3 12 12 14.6 10.7Z" />';
const SPIKE_W = '<path d="M3.6 12 9.4 10.7 12 12 9.4 13.3Z" />';
/** The four intercardinal half-rhumbs, dropped to a hairline so the cardinal
 * reading survives at a favicon size. */
const HALF_RHUMBS =
  '<path d="m5.6 5.6 4.6 4.6" opacity=".5" /><path d="m18.4 18.4-4.6-4.6" opacity=".5" />' +
  '<path d="m18.4 5.6-4.6 4.6" opacity=".5" /><path d="m5.6 18.4 4.6-4.6" opacity=".5" />';
/** The plotted fix at the centre. A stroked circle at r 1.1 reads as a dot at
 * 16px, and it has to be stroked rather than filled: the style sets
 * `.icon { fill: none }`, because a fill in an engraved set reads as a smudge. */
const FIX = '<circle cx="12" cy="12" r="1.1" />';

/** THE ROSE — QUARTERDECK's brand glyph, and the artwork behind both `helm`
 * and `ship-wheel` (Blueprint draws those with the same ship-wheel path, so
 * the set keeps them identical to each other too). */
const ROSE = SPIKE_N + SPIKE_S + SPIKE_E + SPIKE_W + HALF_RHUMBS + FIX;

/** One rose spike, rotated to point somewhere — the directional grammar. The
 * tail rule (a short ruled line back from the centre) is what stops a lone
 * kite reading as a triangle. */
const spike = (d: string, tail: string): string => `<path d="${d}" /><path d="${tail}" opacity=".65" />`;

/**
 * id -> symbol INNER markup (the `<symbol>` wrapper and the viewBox are added
 * by icons.ts, so a set cannot get either wrong).
 */
export const QUARTERDECK_SYMBOLS: Record<string, string> = {
  // ---- the brand + the instrument family ------------------------------
  helm: ROSE,
  'ship-wheel': ROSE,
  compass: '<circle cx="12" cy="12" r="9.6" />' + SPIKE_N + SPIKE_S + SPIKE_E + SPIKE_W + FIX,
  dividers:
    '<path d="M12 3.2 5.4 20.6" /><path d="M12 3.2 18.6 20.6" />' +
    '<path d="M12 1.6a1.6 1.6 0 0 1 0 3.2 1.6 1.6 0 0 1 0-3.2Z" />' +
    '<path d="M8.6 12.2h6.8" opacity=".6" />',
  telescope:
    '<path d="M3.4 15.9 16.2 8.4" /><path d="M5.6 19.6 18.4 12.1" />' +
    '<path d="m3.4 15.9 2.2 3.7" /><path d="m16.2 8.4 2.2 3.7" />' +
    '<path d="M19.4 5.4h3.2v3.6h-3.2Z" />' +
    '<path d="M9.4 18.6 7.8 22.4" /><path d="M12.6 16.8 14.2 22.4" />',
  sonar:
    '<path d="M12 2.4v6.2" /><path d="M9.4 8.6h5.2l-2.6 4.4Z" />' +
    '<path d="M4.2 16.6h15.6" opacity=".7" /><path d="M6.6 19.4h10.8" opacity=".5" />' +
    '<path d="M9 22.2h6" opacity=".35" />',
  porthole:
    '<circle cx="12" cy="12" r="9.6" /><circle cx="12" cy="12" r="6" opacity=".6" />' +
    '<path d="M5.4 6 8 8.6" opacity=".5" /><path d="M18.6 6 16 8.6" opacity=".5" />' +
    '<path d="M5.4 18 8 15.4" opacity=".5" /><path d="M18.6 18 16 15.4" opacity=".5" />',
  lighthouse:
    '<path d="M9 21.4 10.4 8.6h3.2L15 21.4Z" /><path d="M9.6 15.6h4.8" opacity=".6" />' +
    '<path d="M9.4 6.4h5.2v2.2H9.4Z" /><path d="M12 2.2v4.2" />' +
    '<path d="M17.6 8.2h3.6" opacity=".5" /><path d="M2.8 8.2h3.6" opacity=".5" />',
  anchor:
    '<path d="M12 7.6v13.4" /><path d="M9.2 10.4h5.6" />' +
    '<path d="M12 3.4a2.1 2.1 0 0 1 0 4.2 2.1 2.1 0 0 1 0-4.2Z" />' +
    '<path d="M3.6 14.2a8.4 8.4 0 0 0 16.8 0" /><path d="M3.6 14.2h3" /><path d="M20.4 14.2h-3" />',
  buoy:
    '<path d="M12 2.6 15.4 15H8.6Z" /><path d="M6.4 15h11.2l-1.6 6.4H8Z" />' +
    '<path d="M9.4 18.2h5.2" opacity=".6" /><path d="M9.8 8.8h4.4" opacity=".6" />',
  horizon:
    '<path d="M2.2 14.6h19.6" /><path d="M2.2 18.4h19.6" opacity=".5" />' +
    '<path d="M2.2 21.4h19.6" opacity=".3" />' +
    '<path d="M12 2.4 13.9 9.4 12 11.4 10.1 9.4Z" /><path d="M6.6 9.4h10.8" opacity=".6" />',
  ship:
    '<path d="M3.4 15.4h17.2l-2.6 5.8H6Z" /><path d="M6.8 15.4V9.6h10.4v5.8" />' +
    '<path d="M12 9.6V3.4" /><path d="M12 4.4h5.2L12 7.2Z" />',
  chart:
    '<path d="M3.4 3.6h17.2v16.8H3.4Z" /><path d="M3.4 9.2h17.2" opacity=".5" />' +
    '<path d="M3.4 14.8h17.2" opacity=".5" /><path d="M9 3.6v16.8" opacity=".5" />' +
    '<path d="M15 3.6v16.8" opacity=".5" /><path d="m5.4 18.4 5-6.4 4.4 3 4-6" />',
  logbook:
    '<path d="M4.6 3.4h14.8v17.2H4.6Z" /><path d="M8 3.4v17.2" opacity=".6" />' +
    '<path d="M10.6 8h6.2" /><path d="M10.6 12h6.2" opacity=".7" /><path d="M10.6 16h4" opacity=".5" />',
  knot:
    '<path d="M4.2 8.4h15.6v7.2H4.2Z" /><path d="m4.2 8.4 15.6 7.2" opacity=".7" />' +
    '<path d="m19.8 8.4-15.6 7.2" opacity=".7" /><path d="M12 2.6v5.8" /><path d="M12 15.6v5.8" />',
  wave:
    '<path d="M2.4 9.6h4l2-3 3 6 2.6-4.4 2.4 3.4h5.2" />' +
    '<path d="M2.4 16.2h4l2-3 3 6 2.6-4.4 2.4 3.4h5.2" opacity=".6" />',
  tack:
    '<path d="M12 21.6V12" /><path d="M12 2.4 19 8.2 12 12 5 8.2Z" />' +
    '<path d="M8.6 10.1 12 12l3.4-1.9" opacity=".6" />',
  flag:
    '<path d="M6 21.6V2.6" /><path d="M6 3.6h13.4l-3 4.6 3 4.6H6Z" />',
  signal:
    '<path d="M12 20.4v-6.2" /><path d="M9.2 20.4h5.6" />' +
    '<path d="M8.4 11.4a5.1 5.1 0 0 1 7.2 0" opacity=".7" />' +
    '<path d="M5.6 8.2a9.2 9.2 0 0 1 12.8 0" opacity=".5" />' +
    '<path d="M12 14.2a2 2 0 0 1 0-4 2 2 0 0 1 0 4Z" />',
  gauge:
    '<path d="M2.8 18.6a9.6 9.6 0 0 1 18.4 0" /><path d="M2.8 18.6h18.4" opacity=".6" />' +
    '<path d="m12 17.4 5-6.2" /><circle cx="12" cy="17.4" r="1.2" />' +
    '<path d="M4.6 13.4 6.4 14.2" opacity=".5" /><path d="M12 8.4v2" opacity=".5" />' +
    '<path d="M19.4 13.4 17.6 14.2" opacity=".5" />',
  hourglass:
    '<path d="M6.4 2.6h11.2v3.2L12 12l5.6 6.2v3.2H6.4v-3.2L12 12 6.4 5.8Z" />' +
    '<path d="M8.8 19h6.4" opacity=".6" /><path d="M9.6 5.2h4.8" opacity=".6" />',
  scale:
    '<path d="M12 3.2v17.4" /><path d="M6.4 20.6h11.2" /><path d="M4.4 7.4h15.2" />' +
    '<path d="M4.4 7.4 1.8 14h5.2Z" opacity=".8" /><path d="M19.6 7.4 17 14h5.2Z" opacity=".8" />',
  gate:
    '<path d="M3.4 20.6V6.4h17.2v14.2" /><path d="M3.4 10.6h17.2" opacity=".6" />' +
    '<path d="M12 6.4v14.2" opacity=".6" /><path d="M3.4 3.6h17.2" />',

  // ---- directional grammar: the rose's spikes -------------------------
  chev: spike('M12 5.2 16 13.4h-8Z', 'M12 13.4v5.4'),
  'chevron-up': spike('M12 5.2 16 13.4h-8Z', 'M12 13.4v5.4'),
  'chevron-down': spike('M12 18.8 8 10.6h8Z', 'M12 10.6V5.2'),
  'chevrons-right': spike('M18.8 12 10.6 8v8Z', 'M10.6 12H5.2'),
  'arrow-up-right': spike('m18.4 5.6-1.2 8.6-7.4-7.4Z', 'm9.8 6.8-4.2 11.6'),
  'circle-arrow-up': '<circle cx="12" cy="12" r="9.6" />' + spike('M12 5.6 15.4 12.4h-6.8Z', 'M12 12.4v5.8'),

  // ---- the mode pair: the lamp lit, and the lamp shuttered ------------
  sun:
    '<circle cx="12" cy="12" r="4.4" />' +
    '<path d="M12 1.8v2.6" /><path d="M12 19.6v2.6" /><path d="M1.8 12h2.6" /><path d="M19.6 12h2.6" />' +
    '<path d="m4.8 4.8 1.9 1.9" opacity=".7" /><path d="m17.3 17.3 1.9 1.9" opacity=".7" />' +
    '<path d="m19.2 4.8-1.9 1.9" opacity=".7" /><path d="m6.7 17.3-1.9 1.9" opacity=".7" />',
  moon:
    '<path d="M20.2 15.4A8.6 8.6 0 0 1 8.6 3.8 8.8 8.8 0 1 0 20.2 15.4Z" />' +
    '<path d="M13.4 8.6h2.2" opacity=".45" /><path d="M15.8 12.2h2.2" opacity=".45" />',

  // ---- the status ladder: one engraved bezel, seven states ------------
  'st-ring': '<path d="M12 2.6 21.4 12 12 21.4 2.6 12Z" /><path d="M12 7.4 16.6 12 12 16.6 7.4 12Z" opacity=".45" />',
  'st-half': '<path d="M12 2.6 21.4 12 12 21.4 2.6 12Z" /><path d="M12 2.6 21.4 12 12 21.4Z" />',
  'st-check': '<path d="M12 2.6 21.4 12 12 21.4 2.6 12Z" /><path d="m8.2 12.2 2.6 2.6 5-5.2" />',
  'st-cross': '<path d="M12 2.6 21.4 12 12 21.4 2.6 12Z" /><path d="m15.2 8.8-6.4 6.4" /><path d="m8.8 8.8 6.4 6.4" />',
  'st-pause': '<path d="M12 2.6 21.4 12 12 21.4 2.6 12Z" /><path d="M10 9.2v5.6" /><path d="M14 9.2v5.6" />',
  'st-dash':
    '<path d="M12 2.6 16.4 7" /><path d="M17.8 8.4 21.4 12" /><path d="M21.4 12 17.8 15.6" />' +
    '<path d="M16.4 17 12 21.4" /><path d="M12 21.4 7.6 17" /><path d="M6.2 15.6 2.6 12" />' +
    '<path d="M2.6 12 6.2 8.4" /><path d="M7.6 7 12 2.6" />',
  'st-seal':
    '<path d="M12 2.6 21.4 12 12 21.4 2.6 12Z" /><path d="M12 5.6 18.4 12 12 18.4 5.6 12Z" opacity=".5" />' +
    '<path d="m9.4 12.2 2.2 2.2 4-4.2" />',

  // ---- the two intent marks that sit outside the ladder ---------------
  // The dot is a stroked circle, NOT `h.01`: see the zero-length-dot note in
  // the header. This one shipped invisible under butt caps for a fortnight.
  'circle-alert': '<circle cx="12" cy="12" r="9.6" /><path d="M12 7.2v5.4" /><circle cx="12" cy="16.4" r=".9" />',
  'circle-check': '<circle cx="12" cy="12" r="9.6" /><path d="m8.2 12.2 2.6 2.6 5.2-5.6" />',
  'circle-dot': '<circle cx="12" cy="12" r="9.6" />' + FIX,
  'circle-x': '<circle cx="12" cy="12" r="9.6" /><path d="m15.2 8.8-6.4 6.4" /><path d="m8.8 8.8 6.4 6.4" />',
  'circle-help':
    '<circle cx="12" cy="12" r="9.6" />' +
    '<path d="M9.4 9.4a2.6 2.6 0 0 1 5.2 0c0 1.9-2.6 2.2-2.6 3.9" />' +
    '<circle cx="12" cy="16.6" r=".9" />',

  // ---- the completion wave (owner ruling 2026-09-07) -------------------
  // Everything below answers an id this set used to leave on Blueprint art.
  // Same 24 grid, same ruled construction, same hairline-secondary idiom.

  // nav-rail section markers ---------------------------------------------
  'square-terminal':
    '<path d="M2.8 3.4h18.4v17.2H2.8Z" /><path d="M2.8 6.8h18.4" opacity=".5" />' +
    '<path d="m7 10.2 2.6 2.6L7 15.4" /><path d="M12 15.4h5" />',
  library:
    '<path d="M4.2 4.4h3.2v14.2H4.2Z" /><path d="M8.8 4.4H12v14.2H8.8Z" />' +
    '<path d="m14.2 18.4 2.4-13.8 3.4.6-2.4 13.8Z" />' +
    '<path d="M2.8 20.8h18.4" /><path d="M4.2 8.4h3.2" opacity=".5" />' +
    '<path d="M8.8 8.4H12" opacity=".5" />',
  antenna:
    '<path d="M12 3v18.6" /><path d="M9.2 21.6h5.6" />' +
    '<path d="M12 4.4h6.4l-2.1 2.2 2.1 2.2H12Z" />' +
    '<path d="M12 11.4H5.6l2.1 2.2-2.1 2.2H12Z" opacity=".75" />' +
    '<path d="M6.6 3h10.8" opacity=".45" />',
  'notebook-tabs':
    '<path d="M4.6 2.6h14.8v18.8H4.6Z" /><path d="M14.4 2.6v18.8" opacity=".6" />' +
    '<path d="M2.2 6h2.4" /><path d="M2.2 10h2.4" /><path d="M2.2 14h2.4" /><path d="M2.2 18h2.4" />' +
    '<path d="M14.4 7.4h5" opacity=".55" /><path d="M14.4 12h5" opacity=".55" />' +
    '<path d="M14.4 16.6h5" opacity=".55" />',
  'git-pull-request':
    '<circle cx="6.4" cy="6.4" r="2.6" /><circle cx="17.6" cy="17.6" r="2.6" />' +
    '<path d="M6.4 9v12.4" /><path d="M9 6.4h6.8l1.8 1.8v7" />',
  'badge-check':
    '<path d="M9.4 3.2h5.2l3.7 1.5 1.5 3.7v5.2l-1.5 3.7-3.7 1.5H9.4l-3.7-1.5-1.5-3.7V8.4l1.5-3.7Z" />' +
    '<path d="m8.8 12.2 2.4 2.4 4.6-5" />',
  // Three ribs, not five: at 16px the denser hatch closed up into a solid bar
  // in the rail render, which is the same "unrecognisable blob" failure mode
  // the owner reported — measured on the first cut of this glyph.
  container:
    '<path d="M2.8 6.6h18.4v10.8H2.8Z" />' +
    '<path d="M7.4 6.6v10.8" opacity=".55" /><path d="M12 6.6v10.8" opacity=".55" />' +
    '<path d="M16.6 6.6v10.8" opacity=".55" />' +
    '<path d="M2.8 9.2h18.4" opacity=".4" /><path d="M2.8 14.8h18.4" opacity=".4" />',
  siren:
    '<path d="M7 17.4V12a5 5 0 0 1 10 0v5.4" /><path d="M5.4 17.4h13.2v3.4H5.4Z" />' +
    '<path d="M12 2.2v2.4" /><path d="m4.6 5.4 1.8 1.8" opacity=".55" />' +
    '<path d="m19.4 5.4-1.8 1.8" opacity=".55" /><path d="M2.4 12.6h2" opacity=".55" />' +
    '<path d="M19.6 12.6h2" opacity=".55" />',
  workflow:
    '<path d="M3.2 3.2h7.2v7.2H3.2Z" /><path d="M13.6 13.6h7.2v7.2h-7.2Z" />' +
    '<path d="M6.8 10.4v5.2l1.6 1.6h5.2" />',
  activity: '<path d="M2.2 12.6h3.6l2.4-8.6 4 15.8 2.6-7.2h6.8" /><path d="M2.2 20.6h19.6" opacity=".3" />',
  'grid-2x2':
    '<path d="M3.2 3.2h17.6v17.6H3.2Z" /><path d="M12 3.2v17.6" /><path d="M3.2 12h17.6" />' +
    '<path d="M6 6h3.2v3.2H6Z" opacity=".5" /><path d="M14.8 14.8H18V18h-3.2Z" opacity=".5" />',
  house:
    '<path d="M2.6 11 12 3l9.4 8" /><path d="M4.8 9.2v11.6h14.4V9.2" />' +
    '<path d="M9.6 20.8v-6.4h4.8v6.4" /><path d="M2.6 20.8h18.8" opacity=".5" />',
  'panels-top-left':
    '<path d="M3.2 3.2h17.6v17.6H3.2Z" /><path d="M3.2 8.6h17.6" /><path d="M9 20.8V8.6" />',
  crew:
    '<circle cx="9" cy="7.4" r="3.6" /><path d="M5.4 5.4h7.2" opacity=".55" />' +
    '<path d="M2.6 20.8v-1.4l2.4-4.4h8l2.4 4.4v1.4" />' +
    '<path d="M16.2 4.2a3.6 3.6 0 0 1 0 6.4" opacity=".7" />' +
    '<path d="M18 15.2l3.4 4.2v1.4" opacity=".7" />',
  'user-plus':
    '<circle cx="9" cy="7.4" r="3.6" /><path d="M5.4 5.4h7.2" opacity=".55" />' +
    '<path d="M2.6 20.8v-1.4l2.4-4.4h8l2.4 4.4v1.4" />' +
    '<path d="M19.4 6.8v6.4" /><path d="M22.6 10h-6.4" />',

  // the record family: charts, ledgers, dispatches -----------------------
  book:
    '<path d="M6.4 2.6h12v18.8h-12Z" /><path d="M6.4 2.6 4.4 4.6v16.8l2-2" />' +
    '<path d="M4.4 21.4h12" opacity=".55" /><path d="M18.4 9.6h2.4v3.2h-2.4" opacity=".7" />',
  'book-open':
    '<path d="M12 6.8 3 4.2v13.8l9 2.6Z" /><path d="M12 6.8 21 4.2v13.8l-9 2.6Z" />' +
    '<path d="M5.6 8.4h3.8" opacity=".5" /><path d="M14.6 8.4h3.8" opacity=".5" />' +
    '<path d="M5.6 12.2h3.8" opacity=".5" /><path d="M14.6 12.2h3.8" opacity=".5" />',
  scroll:
    '<path d="M6.6 5.6h11.2v12.8H6.6Z" />' +
    '<path d="M6.6 5.6H4.2a1.8 1.8 0 1 1 0-3.6h13.6" />' +
    '<path d="M17.8 18.4h2a1.8 1.8 0 1 1 0 3.6H6.2" />' +
    '<path d="M9.4 9.6h6" opacity=".65" /><path d="M9.4 13.4h6" opacity=".65" />',
  'scroll-text':
    '<path d="M6.6 5.6h11.2v12.8H6.6Z" />' +
    '<path d="M6.6 5.6H4.2a1.8 1.8 0 1 1 0-3.6h13.6" />' +
    '<path d="M17.8 18.4h2a1.8 1.8 0 1 1 0 3.6H6.2" />' +
    '<path d="M9.4 8.6h6" opacity=".65" /><path d="M9.4 11.4h6" opacity=".65" />' +
    '<path d="M9.4 14.2h6" opacity=".65" /><path d="M9.4 17h3.6" opacity=".45" />',
  map:
    '<path d="M2.6 6 9 3.6l6 2.4 6.4-2.4v14.4L15 20.4l-6-2.4-6.4 2.4Z" />' +
    '<path d="M9 3.6v14.4" opacity=".6" /><path d="M15 6v14.4" opacity=".6" />',
  'folder-open':
    '<path d="M2.6 20.6V4.4h6.2l2 2.6h8.6v3.4" /><path d="m5 20.6 3-9h13.4l-3 9Z" />',
  'file-pen':
    '<path d="M13.6 21.4H5.4V2.6h8.6l4.6 4.6v4.2" /><path d="M13.6 2.6v5h5" opacity=".7" />' +
    '<path d="m19.2 11.4 2.4 2.4-8 8-3.4 1 1-3.4Z" /><path d="m16.4 14.2 2.4 2.4" opacity=".55" />',
  'square-pen':
    '<path d="M12.4 3.4H3.4v17.2h17.2v-8.8" />' +
    '<path d="m19.2 2.4 2.4 2.4-10.6 10.6-3.4 1 1-3.4Z" /><path d="m16.4 5.2 2.4 2.4" opacity=".55" />',
  'clipboard-list':
    '<path d="M8 4.4H5.4v17.2h13.2V4.4H16" /><path d="M8.6 2.4h6.8v3.6H8.6Z" />' +
    '<path d="M11.6 11h4.6" /><path d="M11.6 16h4.6" />' +
    '<circle cx="8.6" cy="11" r=".9" /><circle cx="8.6" cy="16" r=".9" />',
  'clipboard-check':
    '<path d="M8 4.4H5.4v17.2h13.2V4.4H16" /><path d="M8.6 2.4h6.8v3.6H8.6Z" />' +
    '<path d="m8.8 14 2.4 2.4 4.4-4.8" />',
  'message-square': '<path d="M2.6 3.6h18.8v13.6H8.2l-5.6 4.6Z" /><path d="M6.4 8h11.2" opacity=".5" />' +
    '<path d="M6.4 12.4h7.6" opacity=".5" />',
  mail:
    '<path d="M2.6 4.6h18.8v14.8H2.6Z" /><path d="m2.6 5.4 9.4 7.4 9.4-7.4" />' +
    '<path d="m2.6 18.6 6.6-5.4" opacity=".45" /><path d="m21.4 18.6-6.6-5.4" opacity=".45" />',
  reply: '<path d="M20.6 18.6v-3.4L18 12.6H3.6" /><path d="m9.4 17.4-5.4-5.4 5.4-5.4" />',
  send: '<path d="M21.4 2.6 2.6 9.6l7.8 3.6 3.6 8.2Z" /><path d="M21.4 2.6 10.4 13.2" opacity=".65" />',
  bookmark: '<path d="M5.6 2.6h12.8v18.8L12 17.2l-6.4 4.2Z" /><path d="M5.6 7.4h12.8" opacity=".45" />',

  // the works: stores, holds, gear ---------------------------------------
  cargo:
    '<path d="M3.4 6.6 12 2.6l8.6 4v10.8L12 21.4l-8.6-4Z" />' +
    '<path d="M12 11 20.6 6.6" opacity=".6" /><path d="M12 11 3.4 6.6" opacity=".6" />' +
    '<path d="M12 11v10.4" opacity=".6" /><path d="m7.7 4.6 8.6 4" opacity=".45" />',
  box:
    '<path d="M3.6 6.6h16.8v14H3.6Z" /><path d="M3.6 10.2h16.8" />' +
    '<path d="m3.6 10.2 16.8 10.4" opacity=".4" /><path d="M20.4 10.2 3.6 20.6" opacity=".4" />' +
    '<path d="M9.6 6.6V3.4h4.8v3.2" opacity=".7" />',
  crane:
    '<path d="M5.4 21.6V3.4" /><path d="M2.8 21.6h5.2" /><path d="M5.4 3.4h13.8" />' +
    '<path d="M5.4 8.2 12.6 3.4" opacity=".55" /><path d="M19.2 3.4v5.4" />' +
    '<path d="M17.6 8.8h3.2l-1.6 2.8Z" />',
  layers:
    '<path d="M12 2.6 21.4 7 12 11.4 2.6 7Z" />' +
    '<path d="m2.6 12 9.4 4.4 9.4-4.4" opacity=".7" />' +
    '<path d="m2.6 17 9.4 4.4 9.4-4.4" opacity=".5" />',
  database:
    '<ellipse cx="12" cy="5" rx="8.4" ry="2.6" />' +
    '<path d="M3.6 5v14a8.4 2.6 0 0 0 16.8 0V5" />' +
    '<path d="M3.6 11.6a8.4 2.6 0 0 0 16.8 0" opacity=".6" />' +
    '<path d="M3.6 16a8.4 2.6 0 0 0 16.8 0" opacity=".45" />',
  server:
    '<path d="M2.6 2.6h18.8v7.2H2.6Z" /><path d="M2.6 14.2h18.8v7.2H2.6Z" />' +
    '<circle cx="6.2" cy="6.2" r=".9" /><circle cx="6.2" cy="17.8" r=".9" />' +
    '<path d="M14.8 6.2h3.8" opacity=".5" /><path d="M14.8 17.8h3.8" opacity=".5" />',
  gear:
    '<path d="M8.4 3.2h7.2l5.2 5.2v7.2l-5.2 5.2H8.4l-5.2-5.2V8.4Z" />' +
    '<circle cx="12" cy="12" r="3.2" />' +
    '<path d="M10.4 3.2V1.4h3.2v1.8" /><path d="M10.4 20.8v1.8h3.2v-1.8" />' +
    '<path d="M20.8 10.4h1.8v3.2h-1.8" /><path d="M3.2 10.4H1.4v3.2h1.8" />',
  hammer:
    '<path d="M8.6 5.6h12v4.8h-12Z" /><path d="M8.6 7.4H6.2L4.8 8.8v1.4h3.8" opacity=".7" />' +
    '<path d="m12.2 10.4-9 9.4 1.9 1.9 9.6-9" /><path d="M14.6 5.6v4.8" opacity=".5" />',
  wrench:
    '<path d="m18.6 2.6 2.6 2.6-3.4 3.4 2 2 3.4-3.4a5.6 5.6 0 0 1-7.4 7.2l-7.4 7.4-2.8-2.8 7.4-7.4a5.6 5.6 0 0 1 7.2-7.4Z" />',
  puzzle:
    '<path d="M3.4 3.4h6v2.2h3.2V3.4h6v6h-2.2v3.2h2.2v6h-6v-2.2H9.4v2.2h-6v-6h2.2V9.6H3.4Z" />',
  'flask-conical':
    '<path d="M9.6 2.6v6.4L4.4 20.2v1.2h15.2v-1.2L14.4 9V2.6Z" />' +
    '<path d="M8.2 2.6h7.6" /><path d="M7 15.4h10" opacity=".6" />',
  microscope:
    '<path d="M3.4 21.6h17.2" /><path d="M6.6 18.4h8.6" />' +
    '<path d="M14.4 18.4a6.4 6.4 0 0 0 0-12.8h-1.2" opacity=".75" />' +
    '<path d="M9.2 11.8h3.6l1.2-1.2V6H8v4.6Z" /><path d="M11.6 6V3.4H8.4V6" />' +
    '<path d="M8.8 14.2h3.4" opacity=".6" />',
  stethoscope:
    '<path d="M5.4 2.6v2.2" /><path d="M10.6 2.6v2.2" />' +
    '<path d="M4 4.8h8v4.4a4 4 0 0 1-8 0Z" />' +
    '<path d="M8 13.2v2.4a4.6 4.6 0 0 0 9.2 0v-3.2" /><circle cx="17.2" cy="9.8" r="2.4" />',
  bug:
    '<path d="M8.4 8h7.2l1.2 3.2v3.6L12 20.4l-4.8-5.6v-3.6Z" />' +
    '<path d="M9.4 8 8 5.2" /><path d="M14.6 8 16 5.2" />' +
    '<path d="M7.4 11.2H3.6" opacity=".65" /><path d="M16.6 11.2h3.8" opacity=".65" />' +
    '<path d="m7.6 15-3.4 2.4" opacity=".65" /><path d="m16.4 15 3.4 2.4" opacity=".65" />' +
    '<path d="M12 9.6v8" opacity=".4" />',
  camera:
    '<path d="M2.6 7.4h4l1.6-2.6h7.6l1.6 2.6h4v12.8H2.6Z" />' +
    '<circle cx="12" cy="13.4" r="3.4" /><path d="M18 10.2h2" opacity=".55" />',
  eye:
    '<path d="M2 12a11.6 11.6 0 0 1 20 0 11.6 11.6 0 0 1-20 0Z" />' +
    '<circle cx="12" cy="12" r="3.2" />',
  shield:
    '<path d="M12 2.4 20.4 5v7.2c0 4.6-3.4 7.6-8.4 9.4-5-1.8-8.4-4.8-8.4-9.4V5Z" />' +
    '<path d="M12 2.4v19.2" opacity=".4" /><path d="M3.6 9.4h16.8" opacity=".4" />',
  bell:
    '<path d="m5.4 17.4 2.2-4.4V9.6a4.4 4.4 0 0 1 8.8 0V13l2.2 4.4Z" />' +
    '<path d="M12 3.2v2.2" /><path d="M10.4 19.6a1.6 1.6 0 0 0 3.2 0" />' +
    '<path d="M9.4 17.4h5.2" opacity=".45" />',
  link:
    '<path d="M9.6 8.4H5.4l-2.8 2.8v1.6l2.8 2.8h4.2" />' +
    '<path d="M14.4 8.4h4.2l2.8 2.8v1.6l-2.8 2.8h-4.2" />' +
    '<path d="M8 12h8" />',
  pin:
    '<path d="M9.4 2.4h5.2v2.2h-1.2v6l2.8 2.4v2.2H7.8v-2.2l2.8-2.4v-6H9.4Z" />' +
    '<path d="M12 15.2v6.4" />',
  assistant:
    '<path d="M10.6 2.8 12.4 9.6l-1.8 2-1.8-2Z" /><path d="M10.6 20.4 8.8 13.6l1.8-2 1.8 2Z" />' +
    '<path d="M18.2 11.6 11.6 13.4l-1-1.8 1-1.8Z" /><path d="M3 11.6 9.6 9.8l1 1.8-1 1.8Z" />' +
    '<path d="M19.6 3.2v3.2" opacity=".7" /><path d="M18 4.8h3.2" opacity=".7" />',

  // the working grammar: actions, arrows, chrome -------------------------
  search:
    '<path d="M7.4 2.8h5.6L16.2 6v5.6L13 14.8H7.4L4.2 11.6V6Z" /><path d="m14.6 13.4 6.4 6.4" />',
  plus:
    '<path d="M12 4.2v15.6" /><path d="M4.2 12h15.6" />' +
    '<path d="M10.6 4.2h2.8" opacity=".55" /><path d="M10.6 19.8h2.8" opacity=".55" />' +
    '<path d="M4.2 10.6v2.8" opacity=".55" /><path d="M19.8 10.6v2.8" opacity=".55" />',
  x:
    '<path d="m5 5 14 14" /><path d="m19 5-14 14" />' +
    '<path d="M4 6.2 6.2 4" opacity=".55" /><path d="M17.8 20 20 17.8" opacity=".55" />' +
    '<path d="M17.8 4 20 6.2" opacity=".55" /><path d="M4 17.8 6.2 20" opacity=".55" />',
  check: '<path d="m3.6 12.4 5.4 5.4L20.4 6.4" /><path d="M20.4 6.4v2.8" opacity=".5" />',
  copy: '<path d="M8.4 8.4h12.2v12.2H8.4Z" /><path d="M5.6 15.6H3.4V3.4h12.2v2.2" />',
  'trash-2':
    '<path d="M3.4 6h17.2" /><path d="M5.6 6h12.8l-1.2 15.4H6.8Z" />' +
    '<path d="M9.6 6V3.4h4.8V6" />' +
    '<path d="M10.2 10.4v7" opacity=".65" /><path d="M13.8 10.4v7" opacity=".65" />',
  pencil:
    '<path d="m3.4 20.6 1.2-4.2L16.8 4.2l3 3L7.6 19.4Z" />' +
    '<path d="m14.8 6.2 3 3" opacity=".55" />' +
    '<path d="m16.8 4.2 1.8-1.8 3 3-1.8 1.8" />',
  ban: '<circle cx="12" cy="12" r="9.6" /><path d="m5.2 5.2 13.6 13.6" />',
  play: '<path d="M6.4 3.6 20 12 6.4 20.4Z" />',
  square: '<path d="M3.4 3.4h17.2v17.2H3.4Z" /><path d="M6.6 6.6h10.8v10.8H6.6Z" opacity=".45" />',
  terminal: '<path d="m4 5.4 6.6 6.6L4 18.6" /><path d="M12.6 18.6h8.4" />',
  ellipsis: '<circle cx="5" cy="12" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" />',
  filter: '<path d="M2.6 3.6h18.8L14 12.4v7.4l-4 2.2v-9.6Z" />',
  star: '<path d="M12 2.4 14.9 9h7.1l-5.7 4.6 2.1 7.4L12 16.8l-6.4 4.2 2.1-7.4L2 9h7.1Z" />',
  zap: '<path d="M13.4 2.4 4 13.4h6.2l-.4 8.2L20 10.6h-6.2Z" />',
  clock:
    '<circle cx="12" cy="12" r="9.6" /><path d="M12 6.4V12l4 2" />' +
    '<path d="M12 2.4v1.8" opacity=".45" /><path d="M12 19.8v1.8" opacity=".45" />' +
    '<path d="M2.4 12h1.8" opacity=".45" /><path d="M19.8 12h1.8" opacity=".45" />',
  history:
    '<path d="M3.4 12a8.6 8.6 0 1 0 2.5-6.1L3.4 8.4" /><path d="M3.4 3v5.4h5.4" />' +
    '<path d="M12 7.2V12l3.6 1.8" />',
  'rotate-cw':
    '<path d="M20.6 12a8.6 8.6 0 1 1-2.5-6.1L20.6 8.4" /><path d="M20.6 3v5.4h-5.4" />',
  'refresh-cw':
    '<path d="M3.4 12a8.6 8.6 0 0 1 14.7-6.1l2.5 2.5" /><path d="M20.6 3v5.4h-5.4" />' +
    '<path d="M20.6 12a8.6 8.6 0 0 1-14.7 6.1L3.4 15.6" /><path d="M3.4 21v-5.4h5.4" />',
  'list-restart':
    '<path d="M2.6 5h18.8" /><path d="M2.6 12h4.8" /><path d="M2.6 19h4.8" />' +
    '<path d="M12.4 20.4a5 5 0 1 0-4.6-6.8" /><path d="M6.6 10.4v3.6h3.6" />',
  'git-branch':
    '<circle cx="6.4" cy="17.6" r="2.6" /><circle cx="17.6" cy="6.4" r="2.6" />' +
    '<path d="M6.4 15V2.6" /><path d="M6.4 10.4h6.2l2.6 -2.6" />',
  'git-merge':
    '<circle cx="6.4" cy="6.4" r="2.6" /><circle cx="17.6" cy="17.6" r="2.6" />' +
    '<path d="M6.4 9v12.4" /><path d="M6.4 12.4h5.2l3.6 3.6" />',
  'log-in': '<path d="M13.4 3.4h7.2v17.2h-7.2" /><path d="M3.4 12h11.2" /><path d="m10.2 7.4 4.8 4.6-4.8 4.6" />',
  'log-out': '<path d="M10.6 3.4H3.4v17.2h7.2" /><path d="M9.4 12h11.2" /><path d="m16.4 7.4 4.8 4.6-4.8 4.6" />',
  download: '<path d="M12 2.6v11.8" /><path d="m6.6 9.6 5.4 5.4 5.4-5.4" /><path d="M3.4 15.4v6h17.2v-6" />',
  upload: '<path d="M12 15V3.2" /><path d="m6.6 8.6 5.4-5.4 5.4 5.4" /><path d="M3.4 15.4v6h17.2v-6" />',
  'external-link':
    '<path d="M14.6 3.4h6v6" /><path d="M9.6 14.4 20.6 3.4" />' +
    '<path d="M17.6 13.4v7.2H3.4V6.4h7.2" />',
  'undo-2': '<path d="m9.4 14.4-5.4-5.4 5.4-5.4" /><path d="M4 9h10.6l3.4 3.4v4.4l-3.4 3.4H11" />',
  'maximize-2':
    '<path d="M14.6 3.4h6v6" /><path d="m20.6 3.4-6.6 6.6" />' +
    '<path d="m3.4 20.6 6.6-6.6" /><path d="M9.4 20.6h-6v-6" />',
  'minimize-2':
    '<path d="m14 10 6.6-6.6" /><path d="M20 10h-6V4" />' +
    '<path d="m3.4 20.6 6.6-6.6" /><path d="M4 14h6v6" />',
  'arrow-left': spike('M3.6 12 11.8 8v8Z', 'M11.8 12h6.6'),
  'arrow-right': spike('M20.4 12 12.2 8v8Z', 'M12.2 12H5.6'),
  'chevron-left': spike('M5.2 12 13.4 8v8Z', 'M13.4 12h5.4'),

  // ── release-scope SECTION TYPES (#810 PR 4): one per renderer family,
  // cut in this set's own construction so the corpus TOC's section rows are
  // own-set under every style (icons:check refuses a fallback).
  'rs-prose':
    '<path d="M5 3.4h14v17.2H5Z" /><path d="M8 8h8" opacity=".5" />' +
    ' <path d="M8 11.6h8" opacity=".5" /><path d="M8 15.2h5" opacity=".5" />',
  'rs-paired-lists':
    '<path d="M3 4.2h7.6v15.6H3Z" /><path d="M13.4 4.2H21v15.6h-7.6Z" />' +
    ' <path d="M5.4 9h2.8" opacity=".5" /><path d="M5.4 12.6h2.8" opacity=".5" />' +
    ' <path d="M15.8 9h2.8" opacity=".5" /><path d="M15.8 12.6h2.8" opacity=".5" />',
  'rs-table':
    '<path d="M3.2 4.2h17.6v15.6H3.2Z" /><path d="M3.2 9.4h17.6" />' +
    ' <path d="M12 9.4v10.4" opacity=".5" /><path d="M3.2 14.6h17.6" opacity=".5" />',
  'rs-table-expansion':
    '<path d="M3.2 3.6h17.6v9.2H3.2Z" /><path d="M3.2 8.2h17.6" opacity=".5" />' +
    ' <path d="M5.6 15.2h12.8v5.4H5.6Z" opacity=".5" /><path d="m10.4 10.2 1.6 1.6 1.6-1.6" />',
  'rs-rules':
    '<path d="M4.6 5.6h2.4v2.4H4.6Z" opacity=".5" /><path d="M4.6 10.8h2.4v2.4H4.6Z" opacity=".5" />' +
    ' <path d="M4.6 16h2.4v2.4H4.6Z" opacity=".5" /><path d="M10.4 6.8h9.4" />' +
    ' <path d="M10.4 12h9.4" /><path d="M10.4 17.2h6.2" />',
  'rs-state-machine':
    '<path d="M2.6 8.4h7.2v7.2H2.6Z" /><path d="M14.2 8.4h7.2v7.2h-7.2Z" />' +
    ' <path d="M9.8 12h4.4" opacity=".5" /><path d="m12.6 10.4 1.6 1.6-1.6 1.6" />',
  'rs-spec-cards':
    '<path d="M5.4 8.4h15.4v12H5.4Z" /><path d="M3.2 15.2V3.6h11.6" opacity=".5" />' +
    ' <path d="M8.6 12.6h8.6" opacity=".5" /><path d="M8.6 16.2h5.4" opacity=".5" />',
  'rs-wireframe':
    '<path d="M3.2 4.2h17.6v15.6H3.2Z" /><path d="M3.2 8.6h17.6" />' +
    ' <path d="M6.2 11.6h6.2v4.8H6.2Z" opacity=".5" /><path d="M15.2 12.4h2.8" opacity=".5" />' +
    ' <path d="M15.2 15.4h2.8" opacity=".5" />',
};

/** Every id this set re-cuts. Sorted so the gate's output is stable. */
export const QUARTERDECK_ICON_IDS: string[] = Object.keys(QUARTERDECK_SYMBOLS).sort();
